import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Label, Spinner, TextInput } from "flowbite-react";
import React, { useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import AnimatedModal from "../../../../components/AnimatedModal";
import { useAuth } from "../../../../hooks";
import Select from "react-select";
import { useRoles } from "../../../../hooks/get/useRoles";
import {
  customSelectStyles,
  PERMISSION_MAPPER,
} from "../../../../shared/constants";
import { InviteTeamMemberModalProps } from "../interface";
import { checkPermission } from "../../../../lib/utils";

interface FormData {
  email: string;
  roleId: number;
}

// Define the type for role options
interface RoleOptionType {
  value: number;
  label: string;
  description: string;
}

const InviteTeamMemberModal: React.FC<InviteTeamMemberModalProps> = ({
  open,
  onClose,
  onInvite,
  selectedMember,
  isInviting,
  existingMembers = [],
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { data: roles = [], isLoading: isLoadingRoles } = useRoles();

  const roleOptions: RoleOptionType[] = useMemo(
    () =>
      roles.map((role) => ({
        value: role.id,
        label: role.name,
        description: role.description,
      })),
    [roles]
  );

  const isEditMode = !!selectedMember;

  const schema = yup.object().shape({
    email: yup
      .string()
      .required(t("configurations.team.emailRequired"))
      .email(t("configurations.team.invalidEmail"))
      .test(
        "not-self",
        t("configurations.team.cannotInviteYourself"),
        (value) => {
          if (!value || !user?.email) return true;
          return value.toLowerCase() !== user.email.toLowerCase();
        }
      )
      .test(
        "not-existing",
        t("configurations.team.emailAlreadyInvited"),
        (value) => {
          if (!value) return true;
          const members = Array.isArray(existingMembers) ? existingMembers : [];
          return !members.some(
            (member) =>
              member &&
              member.email &&
              member.email.toLowerCase() === value.toLowerCase() &&
              (!selectedMember ||
                (selectedMember.email &&
                  member.email.toLowerCase() !==
                    selectedMember.email.toLowerCase()))
          );
        }
      ),
    roleId: yup.number().required(t("configurations.roles.selectRole")),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver<FormData>(schema),
    defaultValues: {
      email: "",
      roleId: -1,
    },
    mode: "onChange",
  });

  // Reset form when modal opens/closes or when selectedMember changes
  useEffect(() => {
    if (open) {
      if (isEditMode && selectedMember && roles.length) {
        const foundRole = roles.find(
          (role) => role.id === selectedMember.role?.id
        );
        reset({
          email: selectedMember.user?.email || selectedMember.email || "",
          roleId: foundRole?.id || -1,
        });
      } else {
        reset({
          email: "",
          roleId: roleOptions[0]?.value || -1,
        });
      }
    }
  }, [open, selectedMember, isEditMode, roles, roleOptions, reset]);

  const onSubmit = (data: FormData) => {
    onInvite(data.email, data.roleId);
  };

  const hasRoleReadPermission = useMemo(() => {
    return checkPermission(
      PERMISSION_MAPPER.ROLE_MANAGEMENT_READ,
      "modules",
      user?.user_permissions
    );
  }, [user?.user_permissions]);

  return (
    <AnimatedModal
      show={open}
      onClose={onClose}
      title={
        isEditMode
          ? t("configurations.team.edit")
          : t("configurations.team.invite")
      }
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="email" value={t("configurations.team.email")} />
          <TextInput
            id="email"
            type="email"
            {...register("email")}
            disabled={isInviting || isEditMode}
            placeholder="name@company.com"
            color={errors.email ? "failure" : undefined}
            helperText={errors.email?.message}
          />
        </div>

        {hasRoleReadPermission && (
          <div>
            <Label htmlFor="role" value={t("configurations.roles.role")} />
            <Controller
              name="roleId"
              control={control}
              render={({ field }) => (
                <Select<RoleOptionType>
                  id="role"
                  options={roleOptions}
                  styles={{
                    ...(customSelectStyles<RoleOptionType>() as unknown as import("react-select").StylesConfig<
                      RoleOptionType,
                      false,
                      import("react-select").GroupBase<RoleOptionType>
                    >),
                    option: (base, state) => ({
                      ...base,
                      paddingTop: "0.5rem",
                      paddingBottom: "0.5rem",
                      paddingLeft: "0.75rem",
                      paddingRight: "0.75rem",
                      backgroundColor: state.isSelected
                        ? "#f3f4f6"
                        : state.isFocused
                        ? "#f9fafb"
                        : "white",
                      cursor: "pointer",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.125rem",
                    }),
                  }}
                  value={
                    roleOptions.find(
                      (option) => option.value === field.value
                    ) || null
                  }
                  onChange={(option) => {
                    field.onChange(option?.value || -1);
                  }}
                  formatOptionLabel={(option) => (
                    <div className="flex flex-col">
                      <span className="font-medium text-gray-900 text-sm">
                        {option.label}
                      </span>
                      <span className="text-xs text-gray-500 mt-0.5">
                        {option.description || "-"}
                      </span>
                    </div>
                  )}
                  menuPlacement="auto"
                  isSearchable={false}
                  classNamePrefix="custom-select"
                  isDisabled={isInviting || isLoadingRoles}
                  isLoading={isLoadingRoles}
                  placeholder={t("configurations.roles.selectRole")}
                />
              )}
            />
            {errors.roleId && (
              <span className="text-xs text-red-500">
                {errors.roleId.message}
              </span>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-2 mt-6">
          <Button color="light" onClick={onClose} disabled={isInviting}>
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={isInviting || Object.keys(errors).length > 0}
            color={"indigo"}
          >
            <div className="flex items-center gap-2">
              {isInviting && <Spinner color={"purple"} size={"sm"} />}
              {isEditMode
                ? t("common.update")
                : t("configurations.team.invite")}
            </div>
          </Button>
        </div>
      </form>
    </AnimatedModal>
  );
};

export default InviteTeamMemberModal;
